import { useState, useEffect } from "react";
import { toast } from "sonner";
import { CreditCard, ShieldCheck, ShoppingBag, ArrowRight, ArrowLeft, Lock, Loader2 } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { billingApi } from "@/api/billing";
import { useSession } from "@/context/SessionContext";

// STAGE 6 — Unified dual-gateway checkout (mobile-first). "Card Pay" routes to the
// existing PCI-safe Stripe Hosted Checkout redirect; "PayPal" renders live PayPal
// Smart Buttons (Orders v2 create → capture → fulfil). Currency adapts per product
// (coin packs = AUD, subscriptions / Lifetime Pass = USD).
const fmt = (amt, cur) =>
  amt == null ? "—" : (amt / 100).toLocaleString(undefined, { style: "currency", currency: (cur || "usd").toUpperCase() });

export default function S20DualCheckoutPanel({ product, onBack }) {
  const [method, setMethod] = useState("stripe");
  const [busy, setBusy] = useState(false);

  const title = product?.label || "Credit Top-Up Pack";
  const currency = (product?.currency || "aud").toUpperCase();
  const priceLabel = fmt(product?.amount, product?.currency);

  const handleStripe = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { checkout_url } = await billingApi.checkout({
        lookup_key: product.lookup_key,
        origin_url: window.location.origin,
      });
      if (checkout_url) window.location.href = checkout_url;
      else throw new Error("no url");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <div className="absolute inset-0 z-50 flex flex-col bg-[#050505] text-white select-none" data-testid="dual-checkout-panel">
      {/* header */}
      <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3">
        <button onClick={onBack} data-testid="dual-checkout-back" className="grid h-9 w-9 place-items-center rounded-xl border-2 border-white/15 bg-white/5 active:scale-95">
          <ArrowLeft size={16} className="text-white" />
        </button>
        <div className="leading-tight">
          <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-[#FFCC00]">Secure Checkout</p>
          <h2 className="font-serif text-xl text-white">Complete Purchase</h2>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto no-scrollbar p-4 flex flex-col">
        {/* ─── ORDER SUMMARY RECEIPT CARD ─── */}
        <div className="w-full bg-[#0A0A0A] border border-zinc-900 rounded-xl p-4 mb-4" data-testid="dual-checkout-summary">
          <div className="flex items-center gap-2 mb-2">
            <ShoppingBag className="w-4 h-4 text-[#FFCC00]" />
            <span className="text-[9px] text-zinc-500 font-black tracking-widest uppercase">Misteck Invoice Summary</span>
          </div>
          <div className="flex justify-between items-baseline gap-3">
            <h3 className="text-white text-xs font-black uppercase" data-testid="dual-checkout-title">{title}</h3>
            <span className="text-[#FFCC00] font-mono font-black text-base whitespace-nowrap" data-testid="dual-checkout-price">
              {priceLabel} <span className="text-[9px] text-zinc-500">{currency}</span>
            </span>
          </div>
        </div>

        {/* ─── METHOD SELECTOR TOGGLES (44px hit-boxes) ─── */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <button type="button" onClick={() => setMethod("stripe")} data-testid="method-stripe" aria-pressed={method === "stripe"}
            className={`h-11 rounded-xl font-bold text-[11px] uppercase flex items-center justify-center gap-2 transition-colors ${
              method === "stripe" ? "bg-zinc-900 border-2 border-[#FFCC00] text-white" : "bg-[#0D0D0D] border border-zinc-900 text-zinc-500"}`}>
            <CreditCard className="w-4 h-4" /> Card Pay
          </button>
          <button type="button" onClick={() => setMethod("paypal")} data-testid="method-paypal" aria-pressed={method === "paypal"}
            className={`h-11 rounded-xl font-bold text-[11px] uppercase flex items-center justify-center gap-2 transition-colors ${
              method === "paypal" ? "bg-[#003087]/20 border-2 border-[#0070ba] text-[#0070ba]" : "bg-[#0D0D0D] border border-zinc-900 text-zinc-500"}`}>
            <span className="font-black italic text-xs text-[#0070ba]">Pay<span className="text-[#003087]">Pal</span></span>
          </button>
        </div>

        {/* ─── DYNAMIC PAY WINDOW ─── */}
        <div className="flex-1 mb-4">
          {method === "stripe" ? (
            <form onSubmit={handleStripe} className="bg-[#0A0A0A] border border-zinc-900 rounded-xl p-4 space-y-3" data-testid="stripe-pane">
              <span className="text-[9px] text-zinc-500 font-bold uppercase block">Secure Credit / Debit Card</span>
              <div className="flex items-start gap-2 bg-black border border-zinc-800 rounded-lg p-3">
                <Lock className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
                <p className="text-zinc-400 text-[10px] leading-relaxed">
                  You'll be redirected to Stripe's PCI-DSS secure checkout to enter your card details. No card data ever touches Misteck.
                </p>
              </div>
              <p className="text-[8px] text-zinc-600 font-bold uppercase tracking-wide">Sandbox test card · 4242 4242 4242 4242 · any future expiry & CVC</p>
              <button type="submit" disabled={busy} data-testid="stripe-authorize-btn"
                className="w-full h-11 bg-[#FFCC00] hover:bg-white text-black font-black text-xs tracking-wider uppercase rounded-xl flex items-center justify-center gap-2 mt-1 transition-colors disabled:opacity-60">
                {busy ? "Authorising Checkout…" : "Authorize Card Payment"}
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </form>
          ) : (
            <PaypalPane product={product} onBack={onBack} />
          )}
        </div>

        {/* base encryption metadata */}
        <div className="flex items-center justify-between text-zinc-600 text-[8px] font-black uppercase tracking-widest border-t border-zinc-900 pt-3">
          <div className="flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-emerald-600" /> PCI-DSS Compliant Encryption</div>
          <span>Total: {priceLabel} {currency}</span>
        </div>
      </div>
    </div>
  );
}

// ─── LIVE PayPal Smart Buttons (Orders v2) ───
function PaypalPane({ product, onBack }) {
  const { refreshWallet, refreshUser } = useSession();
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    billingApi.paypalConfig()
      .then((d) => (d?.enabled ? setCfg(d) : setErr("PayPal is not available right now.")))
      .catch(() => setErr("Could not reach the PayPal gateway."));
  }, []);

  if (err) {
    return (
      <div className="bg-[#0A0A0A] border border-zinc-900 rounded-xl p-5 text-center flex flex-col justify-center items-center min-h-[160px]" data-testid="paypal-pane">
        <span className="text-[10px] text-red-400 font-bold" data-testid="paypal-error">{err}</span>
      </div>
    );
  }
  if (!cfg) {
    return (
      <div className="bg-[#0A0A0A] border border-zinc-900 rounded-xl p-5 flex justify-center items-center min-h-[160px]" data-testid="paypal-pane">
        <Loader2 className="w-5 h-5 text-[#0070ba] animate-spin" />
      </div>
    );
  }

  const currency = (product?.currency || "usd").toUpperCase();

  return (
    <div className="bg-[#0A0A0A] border border-zinc-900 rounded-xl p-4 min-h-[160px]" data-testid="paypal-pane">
      <span className="text-[9px] text-zinc-500 font-bold tracking-wide uppercase mb-3 block text-center">PayPal Checkout Network</span>
      <PayPalScriptProvider options={{ clientId: cfg.client_id, currency, intent: "capture", components: "buttons" }}>
        <PayPalButtons
          style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal", height: 44 }}
          data-testid="paypal-smart-btn"
          createOrder={async () => {
            const { order_id } = await billingApi.paypalCreateOrder(product.lookup_key);
            return order_id;
          }}
          onApprove={async (data) => {
            try {
              const res = await billingApi.paypalCaptureOrder(data.orderID);
              if (res?.status === "paid") {
                toast.success(product.kind === "coins" ? "Credits added to your wallet." : "Purchase complete — access unlocked.");
                refreshWallet?.(); refreshUser?.();
                window.dispatchEvent(new Event("mist:coins-updated"));
                onBack?.();
              } else {
                toast.error("Payment could not be confirmed.");
              }
            } catch (e) {
              toast.error(e?.response?.data?.detail || "PayPal capture failed.");
            }
          }}
          onError={() => toast.error("PayPal encountered an error. Please try again.")}
          onCancel={() => toast("PayPal checkout canceled.")}
        />
      </PayPalScriptProvider>
      <div className="flex items-start gap-2 bg-black border border-zinc-800 rounded-lg p-3 mt-3">
        <Lock className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
        <p className="text-zinc-400 text-[10px] leading-relaxed">
          You'll approve this payment securely in PayPal. Your PayPal login never touches Misteck.
        </p>
      </div>
    </div>
  );
}
